// front-end/src/components/block-system/block-manager/block-publisher.js

import {
    clearPendingChanges,
    getPendingBlocks,
    getPendingDeleteBlocks,
    hasBlockOrderChanges,
    hasChanges
} from './block-state';
import {checkAuthForBlockOperations, createBlock, deleteBlockById} from './block-api';
import {initCarousel} from './block-renderer';
import {addDeleteButton, deleteBlock} from './block-editor';
import {showErrorNotification, showSuccessNotification} from '../../../utils/notifications';
import {makeBlockDraggable, publishBlockOrders} from './block-sortable';

export async function publishBlockChanges({ silent = false } = {}) {
    if (!checkAuthForBlockOperations()) return;

    if (!hasChanges()) {
        if (!silent) showErrorNotification('No changes to publish');
        return;
    }

    try {
        const pendingBlocks = getPendingBlocks();
        console.log('[Publisher] Pending blocks to publish:', pendingBlocks);

        // Дополнительно логируем отсутствие page_title
        pendingBlocks.forEach(block => {
            if (!block.page_title) {
                console.warn(`[Publisher] Block "${block.data_block_name}" is missing page_title`, block);
            } else {
                console.log(`[Publisher] Block "${block.data_block_name}" has page_title: ${block.page_title}`);
            }
        });

        const pendingDeleteBlocks = getPendingDeleteBlocks();

        // Create table of block names and their order
        pendingBlocks.forEach(block => {
            const blockElement = document.querySelector(`[data-block-name="${block.data_block_name}"]`);
            if (blockElement && blockElement.hasAttribute('data-order')) {
                block.order = parseInt(blockElement.getAttribute('data-order'), 10);
            }
        });

        // Sort blocks by order
        pendingBlocks.sort((a, b) => a.order - b.order);

        if (pendingBlocks.length > 0) {
            const createResults = await Promise.all(
                pendingBlocks.map(block => createBlock(block))
            );

            createResults.forEach((data, index) => {
                const blockData = pendingBlocks[index];
                const blockElement = document.querySelector(`[data-block-name="${blockData.data_block_name}"]`);

                if (blockElement && data.id) {
                    blockElement.setAttribute('data-block-id', data.id);

                    const deleteButton = blockElement.querySelector('.block-delete-button');
                    if (deleteButton) {
                        const newDeleteButton = deleteButton.cloneNode(true);
                        deleteButton.parentNode.replaceChild(newDeleteButton, deleteButton);
                        newDeleteButton.addEventListener('click', (e) => {
                            e.stopPropagation();
                            deleteBlock(blockElement, data.id);
                        });
                    } else {
                        addDeleteButton(blockElement, data.id);
                    }

                    // Dispatch block published event
                    const blockPublishedEvent = new CustomEvent('blockPublished', {
                        detail: {
                            blockElement: blockElement,
                            blockId: data.id,
                            blockData: blockData
                        }
                    });
                    document.dispatchEvent(blockPublishedEvent);
                    console.log('Block published event dispatched for:', blockData.data_block_name);

                    if (!blockElement.hasAttribute('draggable') || blockElement.getAttribute('draggable') !== 'true') {
                        makeBlockDraggable(blockElement);
                    }

                    blockElement.classList.add('block-reordered');
                    setTimeout(() => {
                        blockElement.classList.remove('block-reordered');
                    }, 1000);
                }
            });
        }

        // Publish block order changes
        if (hasBlockOrderChanges()) {
            try {
                const orderPromises = publishBlockOrders();
                if (orderPromises.length > 0) {
                    await Promise.all(orderPromises);
                }
            } catch (orderError) {
                console.error('Error publishing block order changes:', orderError);
                showErrorNotification('Error updating block order');
            }
        }

        // Delete blocks - FIXED: Always pass the block element
        if (pendingDeleteBlocks.length > 0) {
            console.log(`Processing ${pendingDeleteBlocks.length} blocks for deletion`);

            const deletePromises = pendingDeleteBlocks.map(async (item) => {
                try {
                    console.log(`Deleting block ${item.id}, isNewsBlock: ${item.isNewsBlock}, newsId: ${item.newsId}`);

                    // IMPORTANT: Pass the block element to deleteBlockById
                    // This allows the function to check if it's a news block
                    await deleteBlockById(item.id, item.element);

                    if (item.element && item.element.parentNode) {
                        // НОВОЕ: Если это модуль курса, обновляем номера остальных модулей
                        const blockName = item.element.getAttribute('data-block-name');
                        const parentSection = item.element.parentElement;
                        const wasCourseModule = blockName && blockName.includes('course-module-');

                        item.element.remove();

                        // Обновляем номера модулей после удаления
                        if (wasCourseModule && parentSection) {
                            setTimeout(() => {
                                updateModuleNumbersAfterPublishedDelete(parentSection);
                            }, 100);
                        }
                    }
                    return { success: true, id: item.id };
                } catch (error) {
                    console.error(`Error deleting block ${item.id}:`, error);

                    // Restore element display on error
                    if (item.element) {
                        item.element.style.display = item.originalDisplay || 'block';
                    }
                    return { success: false, id: item.id, error };
                }
            });

            const deleteResults = await Promise.all(deletePromises);
            const successCount = deleteResults.filter(result => result.success === true).length;
            const failCount = deleteResults.length - successCount;

            if (failCount > 0) {
                showErrorNotification(`Failed to delete ${failCount} block(s)`);
                console.error('Failed deletions:', deleteResults.filter(r => !r.success));
            }
            if (successCount > 0) {
                console.log(`Successfully deleted ${successCount} block(s)`);
            }
        }

        clearPendingChanges();
        if (!silent) showSuccessNotification('Block changes published successfully!');

        setTimeout(initCarousel, 300);
    } catch (error) {
        console.error('Error publishing blocks:', error);
        showErrorNotification('Error publishing block changes. Check console for details.');
    }
}

// НОВАЯ ФУНКЦИЯ: Обновление номеров модулей после удаления при публикации
function updateModuleNumbersAfterPublishedDelete(parentSection) {
    try {
        // Импортируем функцию обновления модулей
        const { updateAllModuleNumbers } = require('../block-configs/block-types/course-module');
        updateAllModuleNumbers(parentSection);
        console.log('[Publisher] Updated module numbers after published deletion');
    } catch (error) {
        console.error('[Publisher] Error updating module numbers after published deletion:', error);
    }
}